import { lazy, Suspense, useEffect } from 'react'
import type React from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { queryClient } from '@/app/queryClient'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ThemeProvider } from '@/theme/ThemeProvider'
import { useAuthStore } from '@/stores/authStore'

const AppShell = lazy(() => import('@/layouts/AppShell'))
const Login = lazy(() => import('@/pages/Login'))
const HomeDashboard = lazy(() => import('@/pages/HomeDashboard'))
const BillingUsage = lazy(() => import('@/pages/BillingUsage'))
const JobBoard = lazy(() => import('@/pages/JobBoard'))
const Library = lazy(() => import('@/pages/Library'))
const NovelImportPage = lazy(() => import('@/pages/NovelImportPage'))
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail'))
const ProjectCorrections = lazy(() => import('@/pages/ProjectCorrections'))
const ManualPage = lazy(() => import('@/pages/ManualPage'))
const WorkbenchPage = lazy(() => import('@/pages/WorkbenchPage'))
const ProjectList = lazy(() => import('@/pages/ProjectList'))
const ProjectTrash = lazy(() => import('@/pages/ProjectTrash'))
const ReaderPage = lazy(() => import('@/pages/ReaderPage'))
const BranchGraphPage = lazy(() => import('@/pages/BranchGraphPage'))
const AdminSettings = lazy(() => import('@/pages/AdminSettings'))
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'))
const ModelSettings = lazy(() => import('@/pages/ModelSettings'))
const AdminStorageConfig = lazy(() => import('@/pages/AdminStorageConfig'))
const ConsistencyDashboard = lazy(() => import('@/pages/ConsistencyDashboard'))

function RouteFallback() {
  return (
    <div className="grid min-h-screen place-items-center">
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
    </div>
  )
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const status = useAuthStore((s) => s.status)

  if (status === 'unknown') return <RouteFallback />
  if (status === 'guest') return <Navigate to="/login" replace />
  if (!user?.is_admin) {
    return (
      <div className="mx-auto flex min-h-[calc(100vh-3.5rem)] max-w-3xl flex-col justify-center gap-2 p-6">
        <h1 className="text-2xl font-semibold tracking-normal">{t('adminConsole.forbiddenTitle')}</h1>
        <p className="text-sm text-muted-foreground">{t('adminConsole.forbiddenDescription')}</p>
      </div>
    )
  }
  return children
}

export default function App() {
  const bootstrap = useAuthStore((s) => s.bootstrap)

  useEffect(() => {
    const ctrl = new AbortController()
    void bootstrap(ctrl.signal)
    return () => ctrl.abort()
  }, [bootstrap])

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
        <Toaster richColors closeButton />
      </ThemeProvider>
    </QueryClientProvider>
  )
}

function AppRoutes() {
  const location = useLocation()

  return (
    <Suspense fallback={<RouteFallback />}>
      <ErrorBoundary title="页面暂时无法显示" resetKeys={[location.pathname]}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<AppShell />}>
            <Route index element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<HomeDashboard />} />
            <Route path="/projects" element={<ProjectList />} />
            <Route path="/trash" element={<ProjectTrash />} />
            <Route
              path="/novel-import"
              element={(
                <ErrorBoundary title="导入页面暂时无法显示">
                  <NovelImportPage />
                </ErrorBoundary>
              )}
            />
            <Route path="/projects/:id" element={<ProjectDetail />} />
            <Route path="/projects/:id/corrections" element={<ProjectCorrections />} />
            <Route path="/project/:id/corrections" element={<ProjectCorrections />} />
            <Route
              path="/projects/:id/chapters/:cid/workbench/:stage"
              element={(
                <ErrorBoundary title="工作台暂时无法显示">
                  <WorkbenchPage />
                </ErrorBoundary>
              )}
            />
            <Route
              path="/projects/:id/reader"
              element={(
                <ErrorBoundary title="阅读器暂时无法显示">
                  <ReaderPage />
                </ErrorBoundary>
              )}
            />
            <Route
              path="/projects/:id/reader/ch/:n"
              element={(
                <ErrorBoundary title="阅读器暂时无法显示">
                  <ReaderPage />
                </ErrorBoundary>
              )}
            />
            <Route path="/manual" element={<ManualPage />} />
              <Route path="/projects/:id/branches" element={<BranchGraphPage />} />
            <Route path="/board" element={<JobBoard />} />
            <Route path="/library" element={<Library />} />
            <Route path="/consistency" element={<ConsistencyDashboard />} />
            <Route path="/memory" element={<Navigate to="/consistency" replace />} />
            <Route path="/billing" element={<BillingUsage />} />
            <Route path="/admin" element={<RequireAdmin><AdminSettings /></RequireAdmin>} />
            <Route path="/admin/dashboard" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
            <Route path="/admin/models" element={<RequireAdmin><ModelSettings /></RequireAdmin>} />
            <Route path="/admin/storage" element={<RequireAdmin><AdminStorageConfig /></RequireAdmin>} />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Route>
        </Routes>
      </ErrorBoundary>
    </Suspense>
  )
}
